// src/components/MetricCard.jsx
import React from 'react';

const Sparkline = ({ data }) => {
  if (!Array.isArray(data) || data.length < 2) return null;

  const values = data.map((d) => d.value).filter((v) => Number.isFinite(v));
  if (values.length < 2) return null;

  const first = values[0];
  const last = values[values.length - 1];
  const direction = last > first ? "up" : last < first ? "down" : "flat";

  const w = 120;
  const h = 26;
  const pad = 2;

  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const span = maxV - minV || 1;

  const points = values
    .map((v, i) => {
      const x = pad + (i * (w - pad * 2)) / (values.length - 1);
      const y = pad + (h - pad * 2) * (1 - (v - minV) / span);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="metric-sparkline" aria-hidden="true">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <polyline
          className={`sparkline-line ${direction}`}
          fill="none"
          strokeWidth="2"
          points={points}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};
const MetricCard = ({ metric, data, onClick, onInfoClick, sparklineData, sparklineLabel }) => {
  if (!metric) return null;

  const formatValue = (value, unit) => {
    if (value === null || value === undefined || isNaN(value)) return '—';

    const num = Number(value);

    const twoDecimal = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    switch (unit) {
      case 'percent': {
        // Guardrail: if FRED sends index-like values (e.g. 324.37),
        // treat them as "x 100" and scale down so you see 3.24%.
        let v = num;
        if (Math.abs(v) > 50) {
          v = v / 100;
        }
        return `${twoDecimal.format(v)}%`;
      }
      case 'index':
        return twoDecimal.format(num);
      case 'dollar':
        return `$${twoDecimal.format(num)}`;
      case 'trillions': {
        const oneDecimal = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });
        return `$${oneDecimal.format(num)}T`;
      }
      default:
        return twoDecimal.format(num);
    }
  };

  const calculateChange = () => {
    if (!data || data.value === null || data.prevValue === null) return null;
    const change = ((data.value - data.prevValue) / data.prevValue) * 100;
    return {
      value: Math.abs(change).toFixed(2),
      direction: change >= 0 ? 'up' : 'down',
    };
  };

  const change = calculateChange();
  const displayValue = data ? formatValue(data.value, metric.unit) : '—';
  const isError = data?.status === 'error' || data?.value === null;

  return (
    <div
      className={`metric-card ${isError ? 'metric-error' : ''}`}
      onClick={() => onClick && onClick(metric)}
    >
      <div className="metric-header">
        <div className="metric-title-group">
          <div className="metric-icon">{metric.icon}</div>
          <div>
            <div className="metric-title">{metric.title}</div>
            <div className="metric-subtitle">{metric.subtitle}</div>
          </div>
        </div>

        <div className="metric-header-right">
          {change && (
            <div className={`metric-trend trend-${change.direction}`}>
              {change.direction === 'up' ? '↑' : '↓'} {change.value}%
            </div>
          )}

          {onInfoClick && (
            <button
              type="button"
              className="metric-info-button"
              aria-label={`How to read ${metric.title}`}
              onClick={(e) => {
                e.stopPropagation(); // do NOT open the FRED chart
                onInfoClick(metric);
              }}
            >
              i
            </button>
          )}
        </div>
      </div>

      <div className="metric-bottom-row">
        <div className="metric-value">
          {isError ? '—' : displayValue}
        </div>

        {!isError && sparklineData ? (
          <div className="metric-sparkline-wrap">
          <Sparkline data={sparklineData} />
          {sparklineLabel ? (
            <div className="metric-sparkline-label">{sparklineLabel}</div>
          ) : null}
        </div>
        ) : null}
      </div>

    </div>
  );
};

export default MetricCard;
